import React, { useState, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { useGetCategoriesQuery } from "../../../product/api/productTaxonomyApi";
import {
  buildCategoryTree,
  hasCategoryProducts,
  type CategoryWithChildren,
} from "../../../product/helpers/productsCategoryHelpers";
import type { TransformedWPCategory } from "../../../product/types/productTaxonomyApiTypes";

// SVG for the hamburger menu icon
const MenuIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

// SVG for the dropdown arrow
const ArrowIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    viewBox="0 0 16 16"
  >
    <path
      fillRule="evenodd"
      d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
    />
  </svg>
);

// Recursive component to render each level of the category menu
const CategoryLevel: React.FC<{ categories: CategoryWithChildren[] }> = ({
  categories,
}) => (
  <ul className="products-category-dropdown__list">
    {categories.map((category) => {
      const subCategories =
        category.children?.filter(hasCategoryProducts) || [];
      const hasSubcategories = subCategories.length > 0;
      return (
        <li
          key={category.id}
          className={`products-category-dropdown__item ${
            hasSubcategories ? "has-children" : ""
          }`}
        >
          <a href={category.link} className="products-category-dropdown__link">
            {category.name}
          </a>
          {hasSubcategories && <CategoryLevel categories={subCategories} />}
        </li>
      );
    })}
  </ul>
);

// Main Dropdown Component
const ProductsCategoryDropdown: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { data: categoriesData, isLoading } = useGetCategoriesQuery();
  const categoriesArray: TransformedWPCategory[] = categoriesData
    ? Object.values(categoriesData.categories)
    : [];

  const categoryTree = useMemo(
    () => buildCategoryTree(categoriesArray).filter(hasCategoryProducts),
    [categoriesArray]
  );

  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 200);
  }, []);

  return (
    <div
      className="products-category-dropdown"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className="products-category-dropdown__trigger"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <MenuIcon />
        <span>Ürünlerimiz</span>
        <ArrowIcon />
      </button>

      {isOpen && (
        <div className="products-category-dropdown__panel">
          {isLoading ? (
            <div className="products-category-dropdown__skeleton">
              <Skeleton count={8} height={20} style={{ marginBottom: 10 }} />
            </div>
          ) : (
            <CategoryLevel categories={categoryTree} />
          )}
        </div>
      )}
    </div>
  );
};

export default ProductsCategoryDropdown;
